// Shared quick-action helpers used by BulkUpload, ProductList, and BatchDetail.
// Each helper accepts a product-like object and returns { ok, reason?, needsReconnect?, image?, size? }.
// Product shape (flexible):
//   { product_name, barcode, quantity, selling_price|final_price, retail_price, images?, product_desc? }

import { apiCall } from '../AuthContext';

export async function quickPrintProduct(p, templateId) {
  try {
    const res = await apiCall('/api/print-label', {
      method: 'POST',
      data: {
        product_name: p.product_name,
        retail_price: String(p.retail_price || '0'),
        our_price: String(p.final_price ?? p.selling_price ?? '0'),
        barcode: p.barcode || '',
        template_id: templateId,
      },
    });
    if (res.data?.error) return { ok: false, reason: res.data.message || 'Print failed' };
    return { ok: true, image: res.data.image, size: res.data.size };
  } catch (e) {
    return { ok: false, reason: e.response?.data?.detail || e.message || 'Print failed' };
  }
}

export async function quickPushSquareProduct(p) {
  try {
    const hasImg = !!(p.images && p.images[0]);
    await apiCall('/api/push-square', {
      method: 'POST',
      data: {
        barCode: p.barcode || '',
        productName: p.product_name,
        Desc: p.product_desc || '',
        finalPrice: String(p.final_price ?? p.selling_price ?? 0),
        retailPrice: String(p.retail_price || 0),
        quantity: String(p.quantity || 1),
        squareImageUrl: hasImg ? [p.images[0]] : [],
        availableOnline: hasImg,
        sku: p.barcode || '',
      },
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: e.response?.data?.detail || e.message || 'Square push failed' };
  }
}

// `updateMode` (optional): 'full' | 'price_only' | 'inventory_only'
//   • 'full' (default) — sync everything (current behaviour, used by row Push button)
//   • 'price_only' — when the variant already exists, change only price/compareAtPrice;
//     leave inventory, cost, weight, images, collections untouched. Use this from
//     Quick Edit price-modal so a price tweak doesn't bump stock.
//   • 'inventory_only' — adjust inventory only (price untouched).
export async function quickPushShopifyProduct(p, updateMode = 'full') {
  try {
    const hasImg = !!(p.images && p.images[0]);
    const finalPrice = Number(p.final_price ?? p.selling_price ?? 0);
    const retailPrice = Number(p.retail_price || 0);
    const r = await apiCall('/api/push-shopify', {
      method: 'POST',
      data: {
        barCode: p.barcode || '',
        productName: p.product_name,
        Desc: p.product_desc || '',
        finalPrice: String(finalPrice),
        compareAtPrice: retailPrice > finalPrice ? String(retailPrice) : '',
        cost: String(p.cost || 0),
        quantity: String(p.quantity || 1),
        sku: p.barcode || '',
        squareImageUrl: hasImg ? [p.images[0]] : [],
        salesChannels: [],
        updateMode,
      },
    });
    const diag = r.data?.publish_diagnostic;
    if (diag) {
      const pubs = diag.published_to || [];
      const found = diag.publications_found || [];
      if (pubs.length === 0) {
        if (diag.publications_denied) {
          return { ok: false, reason: 'missing read_publications scope — reconnect Shopify', needsReconnect: true };
        }
        if (!found.some(n => (n || '').toLowerCase().includes('pos') || (n || '').toLowerCase().includes('point of sale'))) {
          return { ok: false, reason: `no POS channel (found: ${found.join(', ') || 'none'})` };
        }
        return { ok: false, reason: `publish to POS failed: ${(diag.errors || []).join('; ') || 'unknown'}` };
      }
    }
    return { ok: true, action: r.data?.action, matched_by: r.data?.matched_by, update_mode: r.data?.update_mode };
  } catch (e) {
    return { ok: false, reason: e.response?.data?.detail || e.message || 'Shopify push failed' };
  }
}

// Triggers a native print dialog for a single label PNG (same-origin iframe).
// sizeStr: '2x1' | '3x2' | '4x6' — controls @page size.
export function openSingleLabelPrint(imageDataUrl, title, sizeStr) {
  try {
    const prev = document.getElementById('__single_print_iframe');
    if (prev) prev.remove();
    const sizeMap = { '2x1': '2in 1in', '3x2': '3in 2in', '4x6': '4in 6in' };
    const pageSize = sizeMap[(sizeStr || '2x1').toLowerCase()] || '2in 1in';
    const iframe = document.createElement('iframe');
    iframe.id = '__single_print_iframe';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html><head><title>${title || 'Label'}</title>
<style>
  @page { size: ${pageSize}; margin: 0; }
  html, body { margin: 0; padding: 0; }
  .pg { width: 100vw; height: 100vh; display: flex; align-items: center; justify-content: center; }
  img { width: 100%; height: 100%; object-fit: contain; display: block; }
</style></head><body><div class="pg"><img src="${imageDataUrl}" alt="label" /></div></body></html>`);
    doc.close();
    const doPrint = () => {
      try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) { /* noop */ }
    };
    const img = doc.images[0];
    if (img && img.complete && img.naturalWidth > 0) setTimeout(doPrint, 300);
    else if (img) img.addEventListener('load', () => setTimeout(doPrint, 300));
    else setTimeout(doPrint, 800);
    setTimeout(() => { try { iframe.remove(); } catch (_e) { /* noop */ } }, 30000);
    return true;
  } catch (e) {
    return false;
  }
}
